/**
 * Users administration page.
 */

import { useState } from "react";
import { useUsers, useDeleteUser, useLocations } from "@/api/hooks";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import type { User, UserRole } from "@/api/types";
import { Plus, Loader2, AlertCircle, Pencil, Trash2, UserCircle, MapPin } from "lucide-react";
import { UserDialog } from "../components/users/UserDialog";

const roleLabels: Record<UserRole, string> = {
  ROLE_USER: "Utilisateur",
  ROLE_ADMIN: "Administrateur",
  ROLE_SUPER_ADMIN: "Super Admin",
  ROLE_CATALOGER: "Catalogueur",
  ROLE_CIRCULATION: "Agent de prêt",
  ROLE_ACQUISITION: "Acquisitions",
};

const roleColors: Record<UserRole, string> = {
  ROLE_USER: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
  ROLE_ADMIN: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  ROLE_SUPER_ADMIN: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  ROLE_CATALOGER: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  ROLE_CIRCULATION: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
  ROLE_ACQUISITION: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
};

export function UsersPage() {
  const { data: users, isLoading, error } = useUsers();
  const deleteUser = useDeleteUser();
  const { data: locations } = useLocations();
  const { user: currentUser, isSuperAdmin } = useAuth();
  // undefined: closed, null: creating, a user: editing them
  const [editing, setEditing] = useState<User | null | undefined>(undefined);

  const handleDelete = async (user: User) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${user.fullName} ?`)) {
      try {
        await deleteUser.mutateAsync(user.id);
      } catch {
        // Error handled by mutation
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Utilisateurs</h1>
          <p className="text-muted-foreground">Gérez les comptes utilisateurs de l'application</p>
        </div>
        {isSuperAdmin && (
          <Button
            onClick={() => {
              setEditing(null);
            }}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Nouvel utilisateur
          </Button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-destructive flex flex-col items-center justify-center py-12">
          <AlertCircle className="mb-4 h-12 w-12" />
          <p>Erreur lors du chargement des utilisateurs</p>
        </div>
      ) : (
        <div className="rounded-lg border">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="px-4 py-3 text-left text-sm font-medium">Utilisateur</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Rôles</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Établissement</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Statut</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users?.map((user) => (
                <tr key={user.id} className="hover:bg-muted/25 border-b last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full">
                        <UserCircle className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="font-medium">{user.fullName}</p>
                        <p className="text-muted-foreground text-sm">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.roles
                        .filter((r) => r !== "ROLE_USER")
                        .map((role) => (
                          <span
                            key={role}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${roleColors[role]}`}
                          >
                            {roleLabels[role]}
                          </span>
                        ))}
                      {user.roles.length === 1 && (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${roleColors.ROLE_USER}`}
                        >
                          {roleLabels.ROLE_USER}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {user.location ? (
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="text-muted-foreground h-4 w-4" />
                        <span>{user.location.name}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        user.active
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                          : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                      }`}
                    >
                      {user.active ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Modifier ${user.fullName}`}
                        onClick={() => {
                          setEditing(user);
                        }}
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                      </Button>
                      {isSuperAdmin && user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Supprimer ${user.fullName}`}
                          onClick={() => handleDelete(user)}
                          disabled={deleteUser.isPending}
                        >
                          <Trash2 className="text-destructive h-4 w-4" aria-hidden />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users?.length === 0 && (
            <div className="text-muted-foreground flex flex-col items-center justify-center py-12">
              <UserCircle className="mb-4 h-12 w-12" />
              <p>Aucun utilisateur trouvé</p>
            </div>
          )}
        </div>
      )}

      {editing !== undefined && (
        <UserDialog
          user={editing}
          locations={locations ?? []}
          canManageRoles={isSuperAdmin}
          isSelf={editing !== null && editing.id === currentUser?.id}
          onClose={() => {
            setEditing(undefined);
          }}
          onSaved={() => {
            setEditing(undefined);
          }}
        />
      )}
    </div>
  );
}
